import { useStore } from "@nanostores/react";
import { map } from "nanostores";
import { useCallback, useEffect, useRef, useState } from "react";

import { Entity, getEntity, listTopEntities, searchEntities } from "../api";

const $entityCache = map<Record<string, Entity>>({});
const DEBOUNCE_DELAY = 300; // 300ms debounce delay for search
const MAX_DEFAULT_ITEMS = 10;
const MAX_SEARCH_ITEMS = 10;

export const useEntityCache = (): {
  entities: Record<string, Entity>;
  getEntityById: (id: string) => Entity | undefined;
  isLoading: boolean;
  loadEntityById: (id: string) => Promise<Entity | null>;
  loadOptions: (inputValue: string) => Promise<Entity[]>;
  searchResults: Entity[];
  selectEntity: (entity: Entity) => void;
} => {
  const entities = useStore($entityCache);
  const debounceTimer = useRef<NodeJS.Timeout>();
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Entity[]>([]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return (): void => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const getEntityById = useCallback((id: string): Entity | undefined => {
    return $entityCache.get()[id];
  }, []);

  const cacheEntity = useCallback((entity: Entity): void => {
    $entityCache.setKey(entity.id, entity);
  }, []);

  const selectEntity = useCallback(
    (entity: Entity): void => {
      cacheEntity(entity);
    },
    [cacheEntity],
  );

  const loadEntityById = useCallback(
    async (id: string): Promise<Entity | null> => {
      setIsLoading(true);
      try {
        const cached = getEntityById(id);
        if (cached) {
          return cached;
        }

        const result = await getEntity({ path: { entityId: id } });
        if (result.data) {
          cacheEntity(result.data);
          return result.data;
        }
      } catch (err) {
        console.error("Failed to fetch selected entity:", err);
      } finally {
        setIsLoading(false);
      }

      return null;
    },
    [cacheEntity, getEntityById],
  );

  const performSearch = useCallback(
    async (searchTerm: string): Promise<Entity[]> => {
      try {
        const result = await searchEntities({
          query: {
            searchTerm,
          },
        });
        if (!result.data) return [];
        return result.data.slice(0, MAX_SEARCH_ITEMS);
      } catch (err) {
        console.error("Failed to search entities:", err);
        return [];
      }
    },
    [],
  );

  const loadOptions = useCallback(
    async (inputValue: string): Promise<Entity[]> => {
      // Clear any pending debounce timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // If there's a search term, perform the debounced search
      if (inputValue) {
        return new Promise((resolve) => {
          debounceTimer.current = setTimeout(async () => {
            setIsLoading(true);
            try {
              const results = await performSearch(inputValue);
              setSearchResults(results);
              resolve(results);
            } finally {
              setIsLoading(false);
            }
          }, DEBOUNCE_DELAY);
        });
      }

      // For empty search or initial load, prioritize cached entities
      const cachedEntities = Object.values(entities).slice(
        0,
        MAX_DEFAULT_ITEMS,
      );
      if (cachedEntities.length > 0) {
        setSearchResults([]);
        setIsLoading(false);
        return cachedEntities;
      }

      // If cache is empty, load initial popular entities
      setIsLoading(true);
      try {
        const result = await listTopEntities({
          query: { limit: MAX_DEFAULT_ITEMS },
        });
        const newEntities = result.data || [];

        // Cache the initial popular entities
        newEntities.forEach(cacheEntity);
        setSearchResults([]);
        return newEntities;
      } catch (err) {
        console.error("Failed to load top entities:", err);
        setSearchResults([]);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [entities, cacheEntity, performSearch],
  );

  return {
    entities,
    getEntityById,
    isLoading,
    loadEntityById,
    loadOptions,
    searchResults,
    selectEntity,
  };
};
